import { Avatar } from '@heroui/react';

import { initialsOf } from '../../services/initials';

// Ταυτότητα συντάκτη: εικόνα, ονοματεπώνυμο, και από κάτω το όνομα χρήστη.
//
// **Ο ίδιος μηχανισμός αντικαταστάτη με την κεφαλίδα**, και όχι παρόμοιος: το
// `Avatar.Image` αποδίδεται μόνο όταν υπάρχει διεύθυνση, γιατί το `Avatar.Fallback`
// του HeroUI εμφανίζεται μόνο αν η εικόνα **αποτύχει να φορτώσει** — με `src`
// κενό ή `undefined` δεν γίνεται καν απόπειρα φόρτωσης, άρα δεν υπάρχει αποτυχία,
// άρα ο αντικαταστάτης δεν εμφανίζεται ποτέ και η θέση μένει κενή.
//
// Το όνομα χρήστη μπαίνει **κάτω** από το ονοματεπώνυμο και όχι δίπλα: είναι το
// σταθερό αναγνωριστικό, ενώ το ονοματεπώνυμο μπορεί να συμπίπτει μεταξύ δύο
// ατόμων. Ο αναγνώστης χρειάζεται και τα δύο, με σαφή ιεραρχία.

/**
 * @param textClassName Κλάσεις για τη **στήλη του κειμένου** και μόνο. Υπάρχει για
 *   την κεφαλίδα, όπου το όνομα αποκρύπτεται σε στενή οθόνη ενώ η εικόνα μένει:
 *   χωρίς αυτό, η κεφαλίδα θα χρειαζόταν δεύτερη υλοποίηση του ίδιου συστατικού —
 *   ακριβώς το μοτίβο που το `UserBadge` υπάρχει για να σταματήσει.
 */
export default function UserBadge({ user, size = 'sm', textClassName = 'flex', children }) {
    // **`||` και όχι `??`.** Ο εξυπηρετητής επιστρέφει τα προαιρετικά πεδία ως
    // **κενό αλφαριθμητικό** και όχι ως απουσία τιμής, οπότε το `??` τα αφήνει να
    // περάσουν: το όνομα γινόταν κενό και η θέση του έμενε άδεια, χωρίς κανένα
    // σφάλμα. Φάνηκε όταν η κεφαλίδα πέρασε σε αυτό το συστατικό — ο κώδικας που
    // αντικαταστάθηκε χρησιμοποιούσε `||` και ήταν σωστός.
    const name = user?.fullName || user?.username || '—';
    const handle = user?.username ? `@${user.username}` : null;

    return (
        <div className="flex min-w-0 items-center gap-2">
            <Avatar size={size} className="shrink-0">
                {user?.avatar && <Avatar.Image src={user.avatar} alt="" />}
                {/* **Ο κανόνας ζει σε ένα σημείο** (ΜΛΑ-02): ένα αρχικό ανά λέξη,
                    το πολύ τρία, η παύλα ως διαχωριστικό, και κεφαλαία χωρίς τόνο.
                    Εδώ υπήρχε δικός του υπολογισμός ενός γράμματος, δίπλα σε άλλον
                    στη σελίδα προφίλ: δύο διατυπώσεις της ίδιας σύμβασης. */}
                <Avatar.Fallback>{initialsOf(user)}</Avatar.Fallback>
            </Avatar>

            <div className={`min-w-0 flex-col leading-tight ${textClassName}`}>
                <span className="truncate text-sm font-medium">{name}</span>
                {/* Το όνομα χρήστη παραλείπεται όταν ταυτίζεται με το εμφανιζόμενο
                    όνομα: δύο ίδιες γραμμές η μία κάτω από την άλλη δεν προσθέτουν
                    τίποτα και μοιάζουν με σφάλμα. */}
                {handle && handle !== `@${name}` && (
                    <span className="truncate text-xs text-muted">{handle}</span>
                )}
                {children}
            </div>
        </div>
    );
}
